import { useState, useEffect } from "react";
import { ChatSidebar } from "@/components/chat/ChatSidebar";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatCanvas } from "@/components/chat/ChatCanvas";
import { Composer } from "@/components/chat/Composer";
import { EmptyState } from "@/components/chat/EmptyState";
import { useChat } from "@/hooks/useChat";
import { useAuth } from "@/hooks/useAuth";
import { usePoseidonMonitor } from "@/hooks/usePoseidonMonitor";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

const Index = () => {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { signOut } = useAuth();

  // Poseidon monitors system health silently in background
  usePoseidonMonitor();

  // Close sidebar when switching to mobile
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  const {
    conversations,
    activeConversation,
    activeConversationId,
    isStreaming,
    sendMessage,
    deleteConversation,
    renameConversation,
    pinConversation,
    archiveConversation,
    selectConversation,
    startNewChat,
  } = useChat();

  const handleSelectConversation = (id: string) => {
    selectConversation(id);
    if (isMobile) setSidebarOpen(false);
  };

  const handleNewChat = () => {
    startNewChat();
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="flex h-[100dvh] w-full overflow-hidden bg-background">
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — overlay on mobile, inline on desktop */}
      <div
        className={
          isMobile
            ? `fixed inset-y-0 left-0 z-50 transition-transform duration-200 ${
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`
            : ""
        }
      >
        <ChatSidebar
          conversations={conversations}
          activeId={activeConversationId}
          onSelect={handleSelectConversation}
          onNew={handleNewChat}
          onDelete={deleteConversation}
          onRename={renameConversation}
          onPin={pinConversation}
          onArchive={archiveConversation}
          collapsed={false}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          onSignOut={signOut}
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <ChatHeader
          hasConversation={!!activeConversation}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          showMenu={isMobile}
        />

        {activeConversation && activeConversation.messages.length > 0 ? (
          <ChatCanvas conversation={activeConversation} isStreaming={isStreaming} />
        ) : (
          <EmptyState onSuggest={sendMessage} />
        )}

        <Composer onSend={sendMessage} disabled={isStreaming} />
      </div>
    </div>
  );
};

export default Index;
